import { Component, OnInit, OnDestroy } from '@angular/core';
import * as Highcharts from 'highcharts';
import * as moment from 'moment';
import { MatDialog } from '@angular/material/dialog';
import { MapDialogComponent } from '../map-dialog/map-dialog.component';
import { DataService } from 'src/app/core/services/data-service.service';
import { Subscription } from 'rxjs';
import { createChart, getConversionRate, getMappedData, replaceUnitPlaceholder } from 'src/app/helperModules/chart.helpers';
import { ISerie, IWidget, IndexFrontend } from 'src/app/shared/models/seriesInfo.interfaces';
import { environment } from 'src/environments/environment';

export interface ITide {
  date?: Date;
  value?: number;
  stormSurge?: number;
}

@Component({
  selector: 'app-hydrography',
  templateUrl: './hydrography.component.html',
  styleUrls: ['./hydrography.component.scss'],
})
export class HydrographyComponent implements OnInit, OnDestroy {
  flipped: boolean = false;
  baseVelocityUnit: string;
  selectedVelocityUnit: string;
  isLastValuesModeled: boolean;

  todayTides: ITide[];
  tomorrowTides: ITide[];
  chart: Highcharts.Chart;

  chartSettings;
  createChart;
  seriesSettings = [];
  lastValuesDate: Date;

  projectSettings : {
    project_name: string;
    page_name: string;
    table_name: string;
    url_logo: string;
  } = {
    project_name: "",
    page_name: '',
    table_name: '',
    url_logo: ''
  };

  dataSubscription: Subscription;
  _widgetData: IWidget;
  mappedData: Map<IndexFrontend, ISerie[]> = new Map<IndexFrontend, ISerie[]>();

  constructor(private dataService: DataService, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.dataService.getChartSettings().subscribe((settings: any) => {
      this.chartSettings = settings;
    });

    this.dataService.getSiteSettings().subscribe((settings: any) => {
      this.projectSettings = settings;
      this.projectSettings.project_name = environment.project;
    });

    this.dataSubscription = this.dataService.hidrographyData$.subscribe(widget => {
      if (widget.charts.yAxis.length > 0 && widget.lastValues.length > 0) {
        this._widgetData = widget;
        this.mappedData = getMappedData(this._widgetData);

        // Check if there is any current intensity chart
        if (this.mappedData.has(IndexFrontend.CurrentIntensityChart)) {
          this.baseVelocityUnit = this.mappedData.get(IndexFrontend.CurrentIntensityChart)[0].unit;
          this.selectedVelocityUnit = localStorage.getItem('velocityUnit') || this.dataService.initialVelocityUnit;
        }

        this.isLastValuesModeled = this.dataService.isHidrographyModeled;
        this.lastValuesDate = this._widgetData.lastValues[0].date;
      
        this.createTable();
        this.drawChart();
      }
    });
  }

  drawChart() {
    this.createChart = createChart.bind(this);
    this.createChart(this._widgetData.charts, this.seriesSettings, this.chartSettings, 'nós');

    if (this.seriesSettings.length > 0) {
      this.chartSettings.series = this.seriesSettings;
      if (this.chart === undefined) {
        this.chart = Highcharts.chart('hidrographyChart', this.chartSettings);
      }
      else {
        this.chart.update(this.chartSettings);
      }
    }
  }

  ngOnDestroy(): void { this.dataSubscription?.unsubscribe() }

  createTable() {
    let allTideTableValues = this._widgetData.tables
      .find(s => s.isTide).chartValues
      .map(v => { return { value: v.value, date: v.date} as ITide })

    this.todayTides = allTideTableValues.filter(tideValue => this.isToday(tideValue.date));
    this.tomorrowTides = allTideTableValues.filter(tideValue => this.isTomorrow(tideValue.date));

    let allStormSurgeValues = this._widgetData.tables
      .find(s => s.isTideSurge).chartValues
      .map(v => v.value);

    for (let i = 0; i < allTideTableValues.length - 1; i++) {
      allTideTableValues[i].stormSurge = allStormSurgeValues[i];
    }
  }

  isToday = (someDate) => {
    const today = new Date();
    return (
      someDate.getDate() == today.getDate() &&
      someDate.getMonth() == today.getMonth() &&
      someDate.getFullYear() == today.getFullYear()
    );
  };

  isTomorrow = (someDate) => {
    return (
      someDate.getDate() == moment().add(1, 'd').toDate().getDate() &&
      someDate.getMonth() == moment().add(1, 'd').toDate().getMonth() &&
      someDate.getFullYear() == moment().add(1, 'd').toDate().getFullYear()
    );
  };

  openMap() {
    this.dialog.open(MapDialogComponent, {
      minWidth: '50%',
      maxWidth: '95%',
      minHeight: '50%',
      maxHeight: 'calc(100% - 80px)',
      data: {
        allData: Array.from(this.mappedData.values()).reduce((acc, series) => acc.concat(series)),
      },
      panelClass: 'custom-dialog-container',
    });
  }

  checkExists(indexFrontend: IndexFrontend) : boolean { 
    return this.mappedData.has(indexFrontend);
  }
  
  getRotation(indexFrontend: number): string {
    return this.checkExists(indexFrontend) != null 
      ? `${-45 + this.mappedData.get(indexFrontend)[0].lastValue + 180}deg` 
      : '0deg';
  }

  velocityUnitChanged(newUnit: string) : void {
    localStorage.setItem('velocityUnit', newUnit);
    if (this.baseVelocityUnit !== newUnit) {
      let convertConstant = getConversionRate(this.baseVelocityUnit, newUnit);

      // Converting last values
      if (this.checkExists(IndexFrontend.CurrentIntensityLastValue)) {
        this.mappedData.get(IndexFrontend.CurrentIntensityLastValue)[0].lastValue *= convertConstant;
      }
      
      this.updateChart(newUnit, convertConstant);
      this.selectedVelocityUnit = newUnit;
      this.baseVelocityUnit = newUnit;
    }
  }

  updateChart(newUnit: string, convertConstant: number) : void {
    // Update wind chart values
    let windChartSeries = this.mappedData.get(IndexFrontend.CurrentIntensityChart);
    windChartSeries.forEach(s => { 
      s.chartValues.forEach(v => v.value = v.value * convertConstant);
      this.chartSettings.series.forEach((serie: any) => {
        if (serie.id === s.id) {
          serie.data.forEach((d: any) => d[1] = parseFloat((d[1] * convertConstant).toFixed(2)));
          serie.tooltip = { valueSuffix: ` ${newUnit}` };
        }
      });
    });

    let yAxis = this.chartSettings.yAxis.find(y => y.unit === this.baseVelocityUnit);
    if (yAxis) {
      // Convert min and max
      if (typeof yAxis.max === 'number') yAxis.max *= convertConstant;
      if (typeof yAxis.min === 'number') yAxis.min *= convertConstant;

      // Recalculate ticks if not dynamic
      if (yAxis.tickPositions) {
        const min = yAxis.min || 0;
        const max = yAxis.max || 0;
        const interval = yAxis.tickPositions[1] - yAxis.tickPositions[0];
        const newInterval = interval * convertConstant;

        let newTicks: number[] = [];
        let numTicks = Math.ceil((max - min) / newInterval);
        for (let i = 0; i <= numTicks; i++) {
          let tick = parseFloat((min + i * newInterval).toFixed(2));
          if (tick <= max) {
            newTicks.push(tick);
          }
        }
        if (newTicks[newTicks.length - 1] !== max) newTicks.push(parseFloat(max.toFixed(2)));

        yAxis.tickPositions = newTicks;
      } 
      yAxis.unit = newUnit;
      yAxis.title.text = replaceUnitPlaceholder(yAxis.title.text, newUnit, /\[(.*?)\]/)
    }
    
    this.chart.update(this.chartSettings);
  }

  isDataUnderTolerance() : boolean {
    const flattenedAndFiltered = Array.from(this.mappedData.values())
      .reduce((acc, curr) => acc.concat(curr), [])
      .filter(serie => serie.lastValue !== undefined && serie.lastValue !== null);
      
    let isUnderTolerance: boolean = true;
    for (let i = 0; i < flattenedAndFiltered.length; i++) {
      let serie = flattenedAndFiltered[i];
      let tolerance = moment().subtract(serie.communicationTolerance ?? 86400, 'seconds');
      isUnderTolerance = !moment(serie.date).isBefore(tolerance);
    }
  
    return isUnderTolerance;
  }
}
